import { useEffect, useId, useRef, useState } from 'react';
import { label } from '../lib/format';

/**
 * A filter that accepts several values, as a disclosure over native checkboxes.
 *
 * Native `<select multiple>` is the smaller change and the wrong one: it needs
 * ctrl-click to add a value, gives no sign that it can hold more than one, and
 * discards a whole selection when someone clicks without the modifier. Adrian
 * Roselli's summary of two decades of testing puts both it and the ARIA
 * Authoring Practices listbox in the same category, and lands on this pattern
 * — real checkboxes in a `<fieldset>` — as the one that keeps working.
 *
 * Nielsen Norman's guidance decides the wrapper rather than the control: with
 * "5 or more items" a listbox or dropdown beats loose checkboxes, and for 5-15
 * "use a dropdown if screen space is limited". It is limited here — this bar
 * already holds six controls in two columns and collapses to one on a phone —
 * so the checkboxes live behind a disclosure. Arrangement has only three
 * options and by that rule could stand bare, but it sits between the other two
 * and a control that looks different reads as a different kind of question.
 *
 * The button is the summary: it names what is selected, so the bar can be read
 * without opening anything.
 */
export function CheckboxFilter({ legend, options, selected, onChange, emptyLabel }: {
  legend: string;
  options: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  emptyLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const container = useRef<HTMLDivElement>(null);

  // Closing on an outside click and on Escape are the two exits people try
  // first; without them the panel is dismissible only by hitting the button
  // again, which is not where the pointer is after ticking a box.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (option: string) => {
    onChange(
      selected.includes(option)
        ? selected.filter((member) => member !== option)
        : [...selected, option],
    );
  };

  const summary = selected.length === 0
    ? emptyLabel
    : selected.map((option) => label(option)).join(', ');

  return (
    <div className="checkbox-filter" ref={container}>
      <span className="checkbox-filter__legend">{legend}</span>
      {/* `aria-label` names the button explicitly. The legend beside it is a
          `span`, not a `label`, so nothing associates the two: without this the
          button announces its *values* — "machine learning, research" — and
          never says which filter they belong to. Measured in the accessibility
          tree rather than assumed. */}
      <button
        type="button"
        className="checkbox-filter__button"
        aria-label={`${legend}: ${summary}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {/* Titled as well as shown: several selections overflow one line, and a
            clipped label otherwise leaves no way to read what is filtering. */}
        <span className="checkbox-filter__summary" title={summary}>{summary}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <fieldset className="checkbox-filter__panel" id={panelId}>
          {/* Kept for the accessible group name and hidden visually: the button
              directly above already carries the heading, so on screen the
              legend was a second copy sitting across the panel's top border. */}
          <legend className="visually-hidden">{legend}</legend>
          {options.map((option) => (
            <label key={option} className="checkbox-filter__option">
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggle(option)}
              />
              <span>{label(option)}</span>
            </label>
          ))}
          <button
            type="button"
            className="checkbox-filter__clear"
            onClick={() => onChange([])}
            disabled={selected.length === 0}
          >
            Clear
          </button>
        </fieldset>
      )}
    </div>
  );
}
